import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { bindActionCreators } from 'redux';
import AcceleronDivider from '/@/components/catalog/acceleron-product-detail';
import ProductDescription from '/@/components/catalog/product-description';
import CatalogWarning from '/@/components/catalog-warning';
import Container from '/@/components/container';
import NewMetrics from '/@/components/new-metrics';
import NewMetricsMobile from '/@/components/new-metrics-mobile';
import Tab from '/@/components/new-tab';
import Tabs from '/@/components/new-tabs';
import PrivacyDisclaimer from '/@/components/privacy-disclaimer';
import RadioButtons from '/@/components/radio-buttons';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import {
    useAllCatalogProducts,
    useCatalogProductsById,
    useGetCatalogInternalComparisonChoicesCropProtection,
} from '/@/hooks/catalog';
import { useCPUseRecommendations } from '/@/hooks/crop-protection';
import { _ } from '/@/locale';
import { checkInternal } from '/@/settings/menu.js';
import { setCatalogComparisons as setCatalogComparisonsAction } from '/@/store/catalog/actions';
import {
    getCatalogBrandBySlug,
    getCatalogComercial,
    getCatalogComparisons,
    getCatalogTechnical,
} from '/@/store/catalog/selectors';
import { getAppInstance, getSessionCountry, getUser } from '/@/store/session/selectors';
import useWindowSize from '/@/utils/hooks/window-size';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

const NewProductDetail = ({ type, itemId, zoneId, internal, slug }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const setCatalogComparisons = bindActionCreators(setCatalogComparisonsAction, dispatch);
    const [nutritionalInfoEnable, setNutritionalInfoEnable] = useState(undefined);
    const comparisons = useSelector(getCatalogComparisons);
    const brand = useSelector((state) => getCatalogBrandBySlug(state, slug));
    const country = useSelector(getSessionCountry);

    const { data: catalogProducts, isLoading: isLoadingMetrics } = useCatalogProductsById({
        ids: [itemId, ...comparisons],
        crop: type,
    });
    const useRecommendationsQuery = useCPUseRecommendations(
        {
            productIdIn: [itemId, ...comparisons],
            page: 1,
            pageSize: 10000,
        },
        {
            enabled: type === 'crop_protection',
        }
    );

    const item = useMemo(() => {
        if (catalogProducts) {
            return catalogProducts.find((i) => {
                return i.id === itemId;
            });
        }
    }, [catalogProducts]);
    const isLoading = !item;

    const windowWidth = useWindowSize().width;
    const isMobile = windowWidth <= 900;
    const isDesktopMd = windowWidth > 1024;
    const user = useSelector(getUser);
    const appInstance = useSelector(getAppInstance);

    const comparisonQueryParam = useMemo(
        () => (new URLSearchParams(location.search).get('comparisons') || '').split(',').filter(Boolean).map(Number),
        [location.search]
    );

    const showEveryTabQueryParam = useMemo(
        () => Boolean(new URLSearchParams(location.search).get('showEveryTab') || ''),
        [location.search]
    );

    useEffect(() => {
        setCatalogComparisons(comparisonQueryParam);
    }, [comparisonQueryParam]);

    const allCatalogProductsQuery = useAllCatalogProducts(
        {
            brand: brand.name,
            crop: type,
            zone: zoneId,
            country: country.code,
            cropProtectionCrop: item?.crop?.en,
            cropProtectionProductType: item?.type?.en,
        },
        { enabled: type === 'refuge' || (type === 'crop_protection' && !item) ? false : true }
    );

    const catalogComparisonChoices = useGetCatalogInternalComparisonChoicesCropProtection(item, internal === 'bayer');

    const comparisonChoices = useMemo(() => {
        if (internal === 'bayer') return catalogComparisonChoices;
        if (type === 'refuge') return [];

        if (!allCatalogProductsQuery.data) return [];
        if (type === 'crop_protection') {
            const results = allCatalogProductsQuery.data?.results;
            return results.map((p) => ({ label: p.name, value: p.id })).filter(({ value }) => value !== itemId);
        }

        // Soy/Corn
        const results = allCatalogProductsQuery.data?.results;
        return results.map((p) => ({ label: p.catalogName, value: p.id })).filter(({ value }) => value !== itemId);
    }, [allCatalogProductsQuery.data, catalogComparisonChoices, internal, itemId, type]);

    const parsedCatalogProducts = useMemo(() => {
        return (catalogProducts || []).map((p) => {
            const useRecommendations = (useRecommendationsQuery.data?.results || [])
                .filter((r) => r.product.id === p.id)
                .flatMap((r) => [
                    {
                        id: r.crop ? `${_('Cuando aplicar')} (${_(r.crop?.name)})` : _('Cuando aplicar'),
                        value: r.whenToApply,
                        name: r.crop ? `${_('Cuando aplicar')} (${_(r.crop?.name)})` : _('Cuando aplicar'),
                    },
                    {
                        id: r.crop ? `${_('Dosis')} (${_(r.crop?.name)})` : _('Dosis'),
                        value: r.dose,
                        name: r.crop ? `${_('Dosis')} (${_(r.crop?.name)})` : _('Dosis'),
                    },
                    ...(_(r?.note)
                        ? [
                              {
                                  id: r.crop ? `${_('Nota')} (${_(r.crop?.name)})` : _('Nota'),
                                  value: r.note,
                                  name: r.crop ? `${_('Nota')} (${_(r.crop?.name)})` : _('Nota'),
                              },
                          ]
                        : []),
                ]);
            return {
                ...p,
                product: p,
                features: p.features.concat(useRecommendations),
            };
        });
    }, [catalogProducts, useRecommendationsQuery.data?.results]);

    const comercial = useSelector((state) =>
        getCatalogComercial(state, itemId, allCatalogProductsQuery.data?.results, internal === 'bayer')
    );
    const technical = useSelector((state) =>
        getCatalogTechnical(state, itemId, allCatalogProductsQuery.data?.results, internal === 'bayer')
    );

    useEffect(() => {
        if (parsedCatalogProducts) {
            const profiles = parsedCatalogProducts.map((profile) => {
                return {
                    ...profile,
                    features: profile.nutritionalInfo || [],
                };
            });
            profiles.map((p) => {
                if (p.features.length) {
                    setNutritionalInfoEnable(true);
                }
            });
            if (profiles.length === 1) {
                setNutritionalInfoEnable(Boolean(profiles?.[0]?.features.length));
            }
            if (!nutritionalInfoEnable && tab === 'nutritionalInfo') {
                setTab('caracteristics');
            }
        }
    }, [parsedCatalogProducts]);

    const tabs = [
        {
            id: 'caracteristics',
            component: <Tab key='caracteristics' value='caracteristics' label={_('Características')} wrapped />,
            enable: internal !== 'bayer',
        },
        {
            id: 'defensiveProfile',
            component: <Tab key='defensiveProfile' value='defensiveProfile' label={_('Perfil defensivo')} wrapped />,
            enable:
                internal !== 'bayer' &&
                parsedCatalogProducts &&
                parsedCatalogProducts.filter((p) => p.metrics).length > 0,
        },
        {
            id: 'nutritionalInfo',
            component: (
                <Tab key='nutritionalInfo' value='nutritionalInfo' label={_('Información nutricional')} wrapped />
            ),
            enable: nutritionalInfoEnable,
        },
        {
            id: 'technical',
            component: <Tab key='technical' value='technical' label={_('Características Técnicas')} wrapped />,
            enable: internal === 'bayer' && checkInternal(user, appInstance),
        },
        // { // NOT AVAILABLE FOR NOW
        //     id: 'comercial',
        //     component: <Tab value='comercial' label={_('Características Comerciales')} wrapped />,
        //     enable: internal === 'bayer' && checkInternal(user, appInstance),
        // },
    ].filter((t) => {
        return t.enable;
    });

    const [tab, setTab] = useState(tabs?.[0]?.id);
    const tabsObject = tabs.reduce((pv, cv) => {
        pv[cv.id] = cv;
        return pv;
    }, {});

    const renderItemFeatures = () => {
        if (!parsedCatalogProducts.length) return null;

        return (
            <div className={styles.features}>
                {!isMobile ? (
                    <NewMetrics
                        country={country}
                        dataKey='features'
                        products={parsedCatalogProducts}
                        brand={brand}
                        internal={internal === 'bayer'}
                    />
                ) : (
                    <NewMetricsMobile
                        country={country}
                        dataKey='features'
                        products={parsedCatalogProducts}
                        brand={brand}
                        internal={internal === 'bayer'}
                    />
                )}
            </div>
        );
    };

    const renderCropProfileMetricts = () => {
        const filteredMetrics = parsedCatalogProducts.filter((p) => p.metrics);
        if (!filteredMetrics.length) return null;

        return (
            <div className={styles.metrics}>
                {!isMobile ? (
                    <NewMetrics
                        highlight
                        dots
                        dataKey='metrics'
                        brand={brand}
                        country={country}
                        products={filteredMetrics}
                        internal={internal === 'bayer'}
                    />
                ) : (
                    <NewMetricsMobile
                        internal={internal === 'bayer'}
                        highlight
                        dots
                        dataKey='metrics'
                        brand={brand}
                        country={country}
                        products={filteredMetrics}
                    />
                )}
            </div>
        );
    };

    const renderItemNutritionalInfo = () => {
        if (!parsedCatalogProducts.length > 0) return null;
        if (!parsedCatalogProducts.some((profile) => profile.nutritionalInfo && profile.nutritionalInfo.length > 0))
            return null;

        const profiles = parsedCatalogProducts.map((profile) => {
            return {
                ...profile,
                features: profile.nutritionalInfo || [],
            };
        });

        return (
            <div className={styles.nutritionalInfo}>
                {!isMobile ? (
                    <NewMetrics
                        highlight
                        dataKey='features'
                        products={profiles}
                        brand={brand}
                        country={country}
                        internal={internal === 'bayer'}
                    />
                ) : (
                    <NewMetricsMobile
                        highlight
                        internal={internal === 'bayer'}
                        dataKey='features'
                        products={profiles}
                        brand={brand}
                        country={country}
                    />
                )}
            </div>
        );
    };

    const renderItemComercial = () => {
        if (!comercial) return;
        return (
            <div className={styles.nutritionalInfo}>
                {!isMobile ? (
                    <NewMetrics
                        highlight
                        dataKey='comercial'
                        products={comercial}
                        brand={brand}
                        country={country}
                        internal={internal === 'bayer'}
                    />
                ) : (
                    <NewMetricsMobile
                        internal={internal === 'bayer'}
                        highlight
                        dataKey='comercial'
                        products={comercial}
                        brand={brand}
                        country={country}
                    />
                )}
            </div>
        );
    };

    const renderItemTechnical = () => {
        if (!technical) return;
        return (
            <div className={styles.nutritionalInfo}>
                {!isMobile ? (
                    <NewMetrics
                        highlight
                        dataKey='technical'
                        products={technical}
                        brand={brand}
                        country={country}
                        internal={internal === 'bayer'}
                    />
                ) : (
                    <NewMetricsMobile
                        highlight
                        internal={internal === 'bayer'}
                        dataKey='technical'
                        products={technical}
                        brand={brand}
                        country={country}
                    />
                )}
            </div>
        );
    };

    const desktopMetrics = () => {
        return (
            <Container classNameRoot={styles.container}>
                <Grid container direction='column' spacing={2}>
                    <Grid item xs={12} md={10} sx={{ width: '100%' }}>
                        {comparisonChoices.length > 0 && type !== 'refuge' && (
                            <Fragment>
                                <Grid item xs={12}>
                                    <h2 className={styles.subtitle}>{_('Elegí hasta tres productos para comparar')}</h2>
                                </Grid>
                                <Grid item xs={12}>
                                    <RadioButtons
                                        max={3}
                                        value={comparisons.map(String)}
                                        options={comparisonChoices}
                                        onChange={(selected) => setCatalogComparisons(selected.map(Number))}
                                        theme={settings.app.theme}
                                        className={styles.radioButtons}
                                    />
                                </Grid>
                            </Fragment>
                        )}
                        {!showEveryTabQueryParam &&
                            (type !== 'crop_protection' ||
                                (internal === 'bayer' && checkInternal(user, appInstance))) &&
                            tabs.length > 1 && (
                                <Box sx={{ width: '100%', padding: '30px 0 0 0' }}>
                                    <Tabs
                                        value={tab}
                                        onChange={(ev, v) => setTab(v)}
                                        textColor='primary'
                                        indicatorColor='primary'
                                        aria-label='tabs'
                                        centered
                                        sx={{ width: '100%' }}
                                    >
                                        {tabs.map((t) => {
                                            return t.component;
                                        })}
                                    </Tabs>
                                </Box>
                            )}

                        <Box sx={{ width: '100%', padding: '20px 0' }}>
                            {(showEveryTabQueryParam ||
                                tab === 'caracteristics' ||
                                (type === 'crop_protection' && internal !== 'bayer')) && (
                                <Fragment>
                                    {showEveryTabQueryParam && tabsObject['caracteristics'] && (
                                        <Tabs
                                            textColor='primary'
                                            indicatorColor='primary'
                                            aria-label='tabs'
                                            centered
                                            sx={{ width: '100%', mt: 4 }}
                                        >
                                            {tabsObject['caracteristics'].component}
                                        </Tabs>
                                    )}

                                    {renderItemFeatures()}
                                </Fragment>
                            )}

                            {(showEveryTabQueryParam || tab === 'defensiveProfile') && (
                                <Fragment>
                                    {showEveryTabQueryParam && tabsObject['defensiveProfile'] && (
                                        <Tabs
                                            textColor='primary'
                                            indicatorColor='primary'
                                            aria-label='tabs'
                                            centered
                                            sx={{ width: '100%', mt: 4 }}
                                        >
                                            {tabsObject['defensiveProfile'].component}
                                        </Tabs>
                                    )}
                                    {renderCropProfileMetricts()}
                                </Fragment>
                            )}

                            {(showEveryTabQueryParam || tab === 'nutritionalInfo') && (
                                <Fragment>
                                    {showEveryTabQueryParam && tabsObject['nutritionalInfo'] && (
                                        <Tabs
                                            textColor='primary'
                                            indicatorColor='primary'
                                            aria-label='tabs'
                                            centered
                                            sx={{ width: '100%', mt: 4 }}
                                        >
                                            {tabsObject['nutritionalInfo'].component}
                                        </Tabs>
                                    )}
                                    {renderItemNutritionalInfo()}
                                </Fragment>
                            )}

                            {(showEveryTabQueryParam || tab === 'comercial') && (
                                <Fragment>
                                    {showEveryTabQueryParam && tabsObject['comercial'] && (
                                        <Tabs
                                            textColor='primary'
                                            indicatorColor='primary'
                                            aria-label='tabs'
                                            centered
                                            sx={{ width: '100%', mt: 4 }}
                                        >
                                            {tabsObject['comercial'].component}
                                        </Tabs>
                                    )}
                                    {renderItemComercial()}
                                </Fragment>
                            )}

                            {(showEveryTabQueryParam || tab === 'technical') && (
                                <Fragment>
                                    {showEveryTabQueryParam && tabsObject['technical'] && (
                                        <Tabs
                                            textColor='primary'
                                            indicatorColor='primary'
                                            aria-label='tabs'
                                            centered
                                            sx={{ width: '100%', mt: 4 }}
                                        >
                                            {tabsObject['technical'].component}
                                        </Tabs>
                                    )}
                                    {renderItemTechnical()}
                                </Fragment>
                            )}
                        </Box>
                        {country.code === 'BR' && brand.type === 'crop_protection' && (
                            <Title className={styles.title}>{`${_(
                                'Ver folleto lo va a redireccionar a '
                            )} https://agrofit.agricultura.gov.br/agrofit_cons/principal_agrofit_cons`}</Title>
                        )}
                        {country.code === 'BR' && brand.type === 'crop_protection' && (
                            <CatalogWarning isDetailView className={styles.title} brand={brand} />
                        )}
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const mobileMetrics = () => {
        return (
            <Fragment>
                {comparisonChoices.length > 0 && type !== 'refuge' && (
                    <Container classNameRoot={styles.comparationsContainer}>
                        <Fragment>
                            <h2 className={styles.subtitle}>{_('Elegí hasta tres productos para comparar')}</h2>
                            <RadioButtons
                                max={3}
                                value={comparisons.map(String)}
                                options={comparisonChoices}
                                onChange={(selected) => setCatalogComparisons(selected.map(Number))}
                                theme={settings.app.theme}
                                className={styles.radioButtons}
                            />
                        </Fragment>
                    </Container>
                )}
                {(type !== 'crop_protection' || (internal === 'bayer' && checkInternal(user, appInstance))) &&
                    tabs.length > 1 && (
                        <Box sx={{ width: '100%' }}>
                            <Tabs
                                value={tab}
                                onChange={(ev, v) => setTab(v)}
                                textColor='primary'
                                indicatorColor='primary'
                                aria-label='tabs'
                                centered
                                sx={{ width: '100%' }}
                            >
                                {tabs.map((t) => {
                                    return t.component;
                                })}
                            </Tabs>
                        </Box>
                    )}

                <Container>
                    <Box sx={{ width: '100%' }}>
                        {(tab === 'caracteristics' || (type === 'crop_protection' && internal !== 'bayer')) &&
                            renderItemFeatures()}
                        {tab === 'defensiveProfile' && renderCropProfileMetricts()}
                        {tab === 'nutritionalInfo' && renderItemNutritionalInfo()}
                        {tab === 'comercial' && renderItemComercial()}
                        {tab === 'technical' && renderItemTechnical()}
                    </Box>
                    {country.code === 'BR' && brand.type === 'crop_protection' && (
                        <Title className={styles.title}>{`${_(
                            'Ver folleto lo va a redireccionar a '
                        )} https://agrofit.agricultura.gov.br/agrofit_cons/principal_agrofit_cons`}</Title>
                    )}
                    {country.code === 'BR' && brand.type === 'crop_protection' && (
                        <CatalogWarning isDetailView className={styles.title} brand={brand} />
                    )}
                </Container>
            </Fragment>
        );
    };

    return (
        <Fragment>
            {isLoading ? (
                <Grid container justifyContent='center' alignItems='center' style={{ height: '100vh' }}>
                    <Grid item>
                        <Spinner />
                    </Grid>
                </Grid>
            ) : (
                <Fragment>
                    <Container classNameRoot={styles.container}>
                        <Grid container direction='column' spacing={2}>
                            <Grid item xs={12}>
                                <ProductDescription
                                    type={type}
                                    product={item}
                                    isMobile={isMobile}
                                    isDesktopMd={isDesktopMd}
                                    itemId={itemId}
                                    zoneId={zoneId}
                                    slug={slug}
                                />
                            </Grid>
                        </Grid>
                    </Container>
                    {country.code === 'AR' && brand.type === 'corn' && <AcceleronDivider />}
                    {isLoadingMetrics ? (
                        <Container classNameRoot={styles.container}>
                            <Grid container direction='column' spacing={2}>
                                <Grid item>
                                    <Skeleton variant='rectangular' width='100%' height={50} />
                                </Grid>
                                <Grid item>
                                    <Skeleton variant='rectangular' width='100%' height={300} />
                                </Grid>
                            </Grid>
                        </Container>
                    ) : (
                        <Fragment>
                            {!isMobile ? desktopMetrics() : mobileMetrics()}
                            {internal === 'bayer' && checkInternal(user, appInstance) && (
                                <PrivacyDisclaimer crop='corn' warning={_('Por favor no compartir con terceros.')} />
                            )}
                        </Fragment>
                    )}
                </Fragment>
            )}
        </Fragment>
    );
};
export default NewProductDetail;
